import { useContext, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { FormProvider, Resolver, useForm } from "react-hook-form";
import { PaymentRequestsDto } from "../dtos/PaymentRequestsDto";
import { useFetchPaymentRequests } from "../hooks/fetchDocument";
import { useTranslation } from "react-i18next";
import { PaymentRequestsModel } from "../models/PaymentRequestsModel";
import { NewDocumentModalViewContext } from "src/components/Shared/NewDocumentModalView";
import { NFTabs } from "src/components/Shared/NewForms/NFTabs";
import { NFTab } from "src/components/Shared/NewForms/NFTab";
import DocumentDetailViewToolbar from "src/components/Shared/DetailViews/DocumentDetailViewToolbar";
import DetailViewPostButton from "src/components/Shared/DetailViews/DetailViewPostButton";
import DetailViewCancelButton from "src/components/Shared/DetailViewCancelButton";
import { NFLookupInput } from "src/components/Shared/NewForms/NFLookupInput";
import { NFTabContainer } from "src/components/Shared/NewForms/NFTabContainer";
import { NFTextInput } from "src/components/Shared/NewForms/NFTextInput";
import { NFDateInput } from "src/components/Shared/NewForms/NFDateInput";
import { NFLookupCompany } from "src/components/Shared/NewForms/NFLookupCompany";
import { NFLookupDivision } from "src/components/Shared/NewForms/NFLookupDivision";
import { PaymentRequestPaymentToTheSupplier } from "../OperationTypesDetailsViews/PaymentRequestPaymentToTheSupplier";
import PaymentRequestDisbursmentOfMoneyDetail from "../OperationTypesDetailsViews/PaymentRequestDisbursmentOfMoneyDetail";
import PaymentRequestsCustomsFeeDetail from "../OperationTypesDetailsViews/PaymentRequestsCustomsFeeDetail";
import PaymentRequestsPaymentToEmployeeDetail from "../OperationTypesDetailsViews/PaymentRequestsPaymentToEmployeeDetail";
import PaymentRequestPaymentOnCreditDetail from "../OperationTypesDetailsViews/PaymtnRequestPaymentOnCreditDetail";
import PaymentRequestOperationTypesIssuanceOfLoanDetail from "../OperationTypesDetailsViews/PaymentRequestOperationTypesIssuanceOfLoanDetail";
import PaymentRequestTransferMoneyBetweenDetail from "../OperationTypesDetailsViews/PaymentRequestTransferMoneyBetweenDetail";
import PaymentRequestIssuingLoanToEmployee from "../OperationTypesDetailsViews/PaymentRequestIssuingLoanToEmployee";
import PaymentRequestOperationTypesCurrencyConversionDetail from "../OperationTypesDetailsViews/PaymentRequestOperationTypesCurrencyConversionDetail";
import PaymentRequestRefundOfPaymentDetail from "../OperationTypesDetailsViews/PaymentRequestRefundOfPaymentDetail";
import PaymentRequestInternalTransferDetail from "../OperationTypesDetailsViews/PaymentRequestInternalTransferDetail";
import PaymentRequestOtherDetail from "../OperationTypesDetailsViews/PaymentRequestOtherDetail";
import { NFLookupBaseDocuments } from "src/components/Shared/NewForms/NFLookupBaseDocuments";
import { NFLookupCashflowItem } from "src/components/Shared/NewForms/NFLookupCashflowItem";
import PaymentRequestsDetailPayments from "./PaymentRequestsDetailPayments";
import { PaymentRequestsDetailFooter } from "./PaymentRequestsDetailFooter";
import { EnumHelper } from "src/helpers/EnumHelper";

interface IProps {
  id?: string;
}

export const PaymentRequestsDetail = ({ id }: IProps) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
    formState,
    getValues,
    watch,
    control,
    trigger,
    ...methods
  } = useForm<PaymentRequestsModel>();

  const modalViewContext = useContext(NewDocumentModalViewContext);
  const { postHandler, saveHandler, closeHandler, cancelHandler } = modalViewContext;
  const [tabIndex, setTabIndex] = useState(0);
  const [fileName, setFileName] = useState("Payment Requests");

  const operationType = watch("operationType");

  const { isDocumentLoading, documentModel, postDocument, cancelDocument, updateDocument } = useFetchPaymentRequests({ documentId: id });

  useEffect(() => {
    if (documentModel) {
      reset(documentModel);
    }
  }, [documentModel]);

  useEffect(() => {
    if (formState.isDirty) {
      console.log("formState.isDirty", formState.isDirty);
      updateDocument(getValues());
    }
  }, [formState.isDirty]);

  const handlePost = async () => {
    await postDocument();
  };

  const handleCancel = async () => {
    await cancelDocument();
  };
  // const exportToExcel = () => {
  //   const data = {
  //     ...model
  //   };
  //   const documentInfo = {
  //     title: AppEntities.PaymentRequests.title,
  //     number: model.documentNumber,
  //     date: model.documentDate
  //   };
  //   saveObjectToExcel(data, documentInfo);
  // };

  // const handleUploadFile = (file: File) => {
  //   importFromExcel(file, (data: any) => {
  //     reset(data);
  //   });
  // };
  return (
    <FormProvider
      getValues={getValues}
      setValue={setValue}
      register={register}
      control={control}
      trigger={trigger}
      reset={reset}
      watch={watch}
      handleSubmit={handleSubmit}
      formState={formState}
      {...methods}
    >
      <NFTabs
        onTabChange={(newIndex) => {
          setTabIndex(newIndex);
        }}
      >
        <NFTab title={"Main"} />
        <NFTab title={"Additional"} />
        <NFTab title={"Payments"} />
      </NFTabs>

      <div className={"modal-container-height"}>
        <DocumentDetailViewToolbar>
          <DetailViewPostButton onPost={handlePost} />
          <DetailViewCancelButton onCancel={handleCancel} />
        </DocumentDetailViewToolbar>

        <Row>
          <Col sm={6}>
            <NFLookupInput title={"Operation type"} fieldName={"operationType"} items={EnumHelper.GetPaymentRequestsForOperationTypes()} />
          </Col>
          <Col sm={6}>
            <NFLookupInput title={t("Status")} fieldName={"paymentRequestStatus"} items={EnumHelper.GetPaymentRequestsStatus()} required />
          </Col>
        </Row>

        <NFTabContainer index={0} currentIndex={tabIndex}>
          <Row>
            <Col md={6}>
              <NFTextInput title={t("Document#")} disabled fieldName={"documentNumber"} />
            </Col>
            <Col md={6}>
              <NFDateInput title={t("DocumentDate")} fieldName={"documentDate"} required={true} />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <NFLookupCompany fieldName="companyId" title={t("Company")} required />
            </Col>
            <Col md={6}>
              <NFLookupDivision fieldName="divisionId" title={t("Division")} companyId={watch("companyId")} />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <NFLookupInput title={t("PaymentRequestPriority")} fieldName={"paymentRequestPriority"} items={EnumHelper.GetIncomingCashOperationType()} required />
            </Col>
            <Col md={6}>
              <NFDateInput title={t("PaymentDate")} fieldName={"paymentDate"} required />
            </Col>
          </Row>
          {operationType === 0 && <PaymentRequestPaymentToTheSupplier />}
          {operationType === 1 && <PaymentRequestDisbursmentOfMoneyDetail />}
          {operationType === 2 && <PaymentRequestsCustomsFeeDetail />}
          {operationType === 3 && <PaymentRequestsPaymentToEmployeeDetail />}
          {operationType === 4 && <PaymentRequestPaymentOnCreditDetail />}
          {operationType === 6 && <PaymentRequestTransferMoneyBetweenDetail />}
          {operationType === 7 && <PaymentRequestOperationTypesIssuanceOfLoanDetail />}
          {operationType === 8 && <PaymentRequestIssuingLoanToEmployee />}
          {operationType === 9 && <PaymentRequestOperationTypesCurrencyConversionDetail />}
          {operationType === 10 && <PaymentRequestRefundOfPaymentDetail />}
          {operationType === 11 && <PaymentRequestInternalTransferDetail />}
          {operationType === 12 && <PaymentRequestOtherDetail />}
        </NFTabContainer>
        <NFTabContainer index={1} currentIndex={tabIndex}>
          <Row>
            <Col md={12}>
              <NFLookupBaseDocuments title={t("BaseDocument")} fieldName="contractId" />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <NFLookupCashflowItem fieldName="cashFlowItemId" title={t("CashFlowItem")} required />
            </Col>
            <Col md={6}></Col>
          </Row>
          <Row>
            <Col md={6}>
              <NFTextInput title={t("CreatedUser")} fieldName={"createdUser"} disabled={true} />
            </Col>
            <Col md={6}>
              <NFDateInput title={t("CreatedDate")} fieldName={"createdDate"} required />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <NFTextInput fieldName="comment" title={t("Comment")} />
            </Col>
          </Row>
        </NFTabContainer>

        <NFTabContainer index={2} currentIndex={tabIndex}>
          <PaymentRequestsDetailPayments />
        </NFTabContainer>
      </div>
      <PaymentRequestsDetailFooter />
    </FormProvider>
  );
};
